/**
 * Creates a grid keydown handler that manages breakout and row break logic.
 *
 * When a grid cell's keydown fires (Enter/Tab):
 * 1. If the cell matches the breakout column AND is empty → delegates to
 *    FormDesign's handleDynamicKeyDown (which jumps to the breakout target)
 * 2. If the cell matches the row break column → adds a new row and focuses
 *    the target column in the new row
 * 3. Otherwise → normal FormDesign navigation
 *
 * Default row break column (when not configured) = last visible grid column
 * Default first column (for new-row focus)       = first visible grid column
 * Both are derived dynamically from formFields — no hardcoded values.
 *
 * @param {Object} options
 * @param {number}   options.rowIndex              - Current row index in the grid
 * @param {string}   options.breakoutColumn         - Column configured for grid breakout (from FormDesign)
 * @param {string}   options.rowBreakColumn         - Column configured for row break (from FormDesign)
 * @param {string}   options.rowBreakTarget         - Target column to focus after row break (from FormDesign)
 * @param {Array}    options.formFields             - Form fields array from FormDesign
 * @param {Function} options.addRow                 - Function to add a new row to the grid
 * @param {Function} options.focusField             - Function to focus a specific field by ID
 * @param {Function} options.handleDynamicKeyDown   - FormDesign's handleKeyDown function
 * @returns {Function} A handler function: (event, columnName) => void
 */
export function createGridKeyDownHandler({
  rowIndex,
  breakoutColumn,
  rowBreakColumn,
  rowBreakTarget,
  formFields,
  addRow,
  focusField,
  handleDynamicKeyDown,
}) {
  return (e, columnName) => {
    if (e.key !== "Enter" && e.key !== "Tab") return;

    // Derive grid columns dynamically from the current formFields state
    const gridFields = (formFields || [])
      .filter(
        (f) =>
          f.fieldId.startsWith("grid_") &&
          f.visible !== false &&
          f.enabled !== false,
      )
      .sort((a, b) => (a.tabIndex ?? 9999) - (b.tabIndex ?? 9999));

    const lastGridCol =
      gridFields.length > 0
        ? gridFields[gridFields.length - 1].fieldId.replace("grid_", "")
        : null;
    const firstGridCol =
      gridFields.length > 0
        ? gridFields[0].fieldId.replace("grid_", "")
        : null;

    // 1. Breakout: if this column matches breakout config AND the cell is empty,
    //    delegate to FormDesign which will jump to the breakout target
    const isBreakout =
      breakoutColumn &&
      columnName.toLowerCase() === breakoutColumn.toLowerCase() &&
      (!e.target.value || e.target.value.trim() === "");

    if (isBreakout) {
      handleDynamicKeyDown(e, `grid_${rowIndex}_${columnName}`);
      return;
    }

    // 2. Row Break: if this column matches the row break config (or the last
    //    grid column by default), add a new row and focus the target column
    const effectiveRowBreakCol = rowBreakColumn || lastGridCol;
    if (
      effectiveRowBreakCol &&
      columnName.toLowerCase() === effectiveRowBreakCol.toLowerCase()
    ) {
      e.preventDefault();
      addRow();
      setTimeout(() => {
        const targetCol = rowBreakTarget || firstGridCol;
        if (targetCol) {
          focusField(`grid_${rowIndex + 1}_${targetCol}`);
        }
      }, 50);
      return;
    }

    // 3. Normal navigation: let FormDesign handle the key event
    handleDynamicKeyDown(e, `grid_${rowIndex}_${columnName}`);
  };
}
